import React, { forwardRef } from 'react';
import Proptypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import warning from 'tiny-warning';
import clsx from 'clsx';
import getThemeColor from '../../lib/getThemeColor';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[0],
  },
  contained: {
    boxShadow: theme.shadows[0],
  },
  fullWidth: {
    width: '100%',
  },
  grouped: {
    minWidth: 40,
    textTransform: 'capitalize',
    '&:not(:first-child)': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    '&:not(:last-child)': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  groupedOutlined: {
    '&:not(:first-child)': {
      borderLeftColor: 'transparent',
      marginLeft: -1,
    },
    '&:hover': {
      borderColor: ({ color }) => getThemeColor(theme, color, 'main'),
    },
  },
  groupedContained: {
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderRight: props =>
        theme.palette[props.color]
          ? `1px solid ${theme.palette[props.color].dark}`
          : `1px solid ${theme.palette.grey[400]}`,
      '&.Mui-disabled': {
        borderRight: `1px solid ${theme.palette.action.disabled}`,
      },
    },
  },
}));

const ButtonGroup = forwardRef(function ButtonGroup(props, ref) {
  const { children, color, size, variant, disabled, fullWidth, className } = props;
  const classes = useStyles({ color });
  const buttonClassName = clsx(
    classes.grouped,
    variant === 'outlined' && classes.groupedOutlined,
    variant === 'contained' && classes.groupedContained
  );

  return (
    <div
      ref={ref}
      role="group"
      className={clsx(
        classes.root,
        className,
        variant === 'contained' && classes.contained,
        fullWidth && classes.fullWidth
      )}
    >
      {React.Children.map(children, child => {
        warning(
          React.isValidElement(child),
          'ButtonGroup children need to be valid React elements.'
        );
        warning(
          child.type !== React.Fragment,
          "ButtonGroup component doesn't accept a Fragment as a child, consider providing an array instead."
        );

        if (!React.isValidElement(child)) return null;

        return React.cloneElement(child, {
          className: clsx(buttonClassName, child.props.className),
          disabled: child.props.disabled || disabled,
          color,
          disableFocusRipple: true,
          disableRipple: true,
          fullWidth,
          size,
          variant,
        });
      })}
    </div>
  );
});

ButtonGroup.propTypes = {
  children: Proptypes.node.isRequired,
  className: Proptypes.string,
  color: Proptypes.oneOf([
    'default',
    'primary',
    'secondary',
    'success',
    'info',
    'warning',
    'danger',
  ]),
  variant: Proptypes.oneOf(['outlined', 'contained']),
  disabled: Proptypes.bool,
  fullWidth: Proptypes.bool,
  size: Proptypes.oneOf(['small', 'medium', 'large']),
};

ButtonGroup.defaultProps = {
  className: '',
  color: 'default',
  variant: 'outlined',
  disabled: false,
  fullWidth: false,
  size: 'medium',
};

ButtonGroup.displayName = 'ButtonGroup';

export default ButtonGroup;
